import { HeaderBulkTypes, PalBulkUnitIdentifier } from '@reviews/interfaces';
import { format, isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns';
import * as _ from 'lodash';
import { isEmail, isNumber } from '../../utils/shared.utils';
import { toDate } from 'date-fns-tz';

const REVIEW_DATE_CALENDAR_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const PAL_BULK_MISSING_IDENTIFIER_MESSAGE = 'PAL bulk file must include either UnitID or PmsID.';
export const PAL_BULK_BOTH_IDENTIFIERS_MESSAGE =
    'PAL bulk file cannot include both UnitID and PmsID. Please use only one identifier per file.';

export type PalBulkIdentifierHeaderFailureReason = 'BOTH_IDENTIFIERS' | 'MISSING_IDENTIFIER';

export type ValidatePalBulkCsvHeadersResult =
    | { valid: true; identifier: PalBulkUnitIdentifier }
    | { valid: false; reason: PalBulkIdentifierHeaderFailureReason };

export type ValidateBulkDataOptions = {
    isPalBulkImport?: boolean;
    palUnitIdentifier?: PalBulkUnitIdentifier;
};

const validateReviewDateDefault = (value: string, errors: Array<string>): void => {
    if (_.isEmpty(value)) {
        errors.push('Review Date is required');
        return;
    }
    const date = toDate(_.toString(value));
    if (!isValid(date)) {
        errors.push('Invalid date');
        return;
    }
    const formatDate = format(date, 'yyyy-MM-dd');
    if (!_.isEqual(formatDate, value)) {
        errors.push('Invalid format date');
        return;
    }
    if (!isBefore(date, new Date())) {
        errors.push('Date in the future');
    }
};

/** PAL bulk: validate ReviewDate as a calendar day (avoids TZ/DST false negatives on yyyy-MM-dd). */
const validateReviewDatePalCalendarDay = (value: string, errors: Array<string>): void => {
    const raw = _.trim(_.toString(value));
    if (_.isEmpty(raw)) {
        errors.push('Review Date is required');
        return;
    }
    if (!REVIEW_DATE_CALENDAR_PATTERN.test(raw)) {
        errors.push('Invalid format date');
        return;
    }
    const date = parse(raw, 'yyyy-MM-dd', new Date());
    if (!isValid(date) || format(date, 'yyyy-MM-dd') !== raw) {
        errors.push('Invalid date');
        return;
    }
    if (isAfter(date, startOfDay(new Date()))) {
        errors.push('Date in the future');
    }
};

export const validateBulkData = (
    header: HeaderBulkTypes,
    value: string,
    errors: Array<string>,
    options?: ValidateBulkDataOptions
): void => {
    const isPalBulkImport = options?.isPalBulkImport === true;
    const palUnitIdentifier = options?.palUnitIdentifier;
    const trimmed = _.trim(value);
    let condition: boolean;
    switch (header) {
        case HeaderBulkTypes.DISPLAY_REVIEW:
            condition = isNumber(value) && (_.toNumber(value) === 1 || _.toNumber(value) === 0);
            if (!condition) {
                errors.push('Display review must be just number 1 or 0');
            }
            break;
        case HeaderBulkTypes.GUEST_EMAIL:
            if (isPalBulkImport && trimmed === '') {
                break;
            }
            if (!isEmail(value)) {
                errors.push('Invalid email');
            }
            break;
        case HeaderBulkTypes.GUEST_FIRST_NAME:
            if (isNumber(value)) {
                errors.push('GuestFirstName no numbers in name');
            }
            break;
        case HeaderBulkTypes.GUEST_LAST_NAME:
            if (isPalBulkImport && trimmed === '') {
                break;
            }
            if (isNumber(value)) {
                errors.push('GuestLastName no numbers in last name');
            }
            break;
        case HeaderBulkTypes.OVERALL_RATING:
            condition = isNumber(value) && _.toNumber(value) > 0 && _.toNumber(value) <= 5;
            if (!condition) {
                errors.push('Overall must be a number between 1 - 5');
            }
            break;
        case HeaderBulkTypes.PUBLIC_REVIEW:
        case HeaderBulkTypes.PUBLIC_REVIEW_TITLE:
        case HeaderBulkTypes.REVIEW_RESPONSE:
            break;
        case HeaderBulkTypes.REVIEW_DATE: {
            const reviewDateErrorCountBefore = errors.length;
            validateReviewDateDefault(value, errors);
            if (isPalBulkImport && errors.length > reviewDateErrorCountBefore) {
                const palReviewDateErrors: Array<string> = [];
                validateReviewDatePalCalendarDay(value, palReviewDateErrors);
                errors.splice(reviewDateErrorCountBefore);
                if (palReviewDateErrors.length > 0) {
                    errors.push(...palReviewDateErrors);
                }
            }
            break;
        }
        case HeaderBulkTypes.UNIT_ID:
            if (isPalBulkImport && palUnitIdentifier === 'pms_id') {
                break;
            }
            if (!isNumber(value)) {
                errors.push('UnitID must be just number');
            }
            break;
        case HeaderBulkTypes.PMS_ID:
            if (!isPalBulkImport || palUnitIdentifier !== 'pms_id') {
                break;
            }
            if (trimmed === '') {
                errors.push('PmsID is required');
            }
            break;
    }
};

const sharedRequiredHeaders = [
    HeaderBulkTypes.REVIEW_DATE,
    HeaderBulkTypes.GUEST_EMAIL,
    HeaderBulkTypes.GUEST_FIRST_NAME,
    HeaderBulkTypes.GUEST_LAST_NAME,
    HeaderBulkTypes.OVERALL_RATING,
    HeaderBulkTypes.DISPLAY_REVIEW,
    HeaderBulkTypes.PUBLIC_REVIEW_TITLE,
    HeaderBulkTypes.PUBLIC_REVIEW,
];

const requiredHeaders = [HeaderBulkTypes.UNIT_ID, ...sharedRequiredHeaders];

/** Required CSV columns for default (non-PAL) bulk upload. */
export const REQUIRED_BULK_CSV_HEADER_KEYS: string[] = [...requiredHeaders];

export const hasRequiredBulkCsvHeaders = (parsedHeaderKeys: string[]): boolean => {
    return REQUIRED_BULK_CSV_HEADER_KEYS.every((key) => parsedHeaderKeys.includes(key));
};

/** Non-PAL bulk imports always require UnitID; PmsID is not a valid substitute. */
export const hasValidNonPalBulkCsvHeaders = (parsedHeaderKeys: string[]): boolean => {
    if (!parsedHeaderKeys.includes(HeaderBulkTypes.UNIT_ID)) {
        return false;
    }
    return hasRequiredBulkCsvHeaders(parsedHeaderKeys);
};

/**
 * PAL-only XOR validation for UnitID vs PmsID.
 * Must not be used when External (PAL) is disabled.
 */
export const validatePalBulkCsvHeaders = (parsedHeaderKeys: string[]): ValidatePalBulkCsvHeadersResult => {
    const hasUnitId = parsedHeaderKeys.includes(HeaderBulkTypes.UNIT_ID);
    const hasPmsId = parsedHeaderKeys.includes(HeaderBulkTypes.PMS_ID);

    if (hasUnitId && hasPmsId) {
        return { valid: false, reason: 'BOTH_IDENTIFIERS' };
    }
    if (!hasUnitId && !hasPmsId) {
        return { valid: false, reason: 'MISSING_IDENTIFIER' };
    }

    return { valid: true, identifier: hasUnitId ? 'unit_id' : 'pms_id' };
};

export const hasRequiredPalBulkCsvHeaders = (parsedHeaderKeys: string[], palUnitIdentifier: PalBulkUnitIdentifier): boolean => {
    const identifierHeader = palUnitIdentifier === 'unit_id' ? HeaderBulkTypes.UNIT_ID : HeaderBulkTypes.PMS_ID;
    const requiredPalHeaders = [identifierHeader, ...sharedRequiredHeaders];
    return requiredPalHeaders.every((key) => parsedHeaderKeys.includes(key));
};

/**
 * Resolves PAL unit identifier mode only when PAL bulk import is enabled.
 * Non-PAL uploads never return a pal_unit_identifier.
 */
export const detectPalUnitIdentifier = (isPalBulkImport: boolean, parsedHeaderKeys: string[]): PalBulkUnitIdentifier | null => {
    if (!isPalBulkImport) {
        return null;
    }
    const palHeaderResult = validatePalBulkCsvHeaders(parsedHeaderKeys);
    return palHeaderResult.valid ? palHeaderResult.identifier : null;
};

export const getPalBulkIdentifierHeaderErrorMessage = (reason: PalBulkIdentifierHeaderFailureReason): string => {
    return reason === 'BOTH_IDENTIFIERS' ? PAL_BULK_BOTH_IDENTIFIERS_MESSAGE : PAL_BULK_MISSING_IDENTIFIER_MESSAGE;
};

export const getBulkCsvTemplateHeaders = (
    isPalBulkImport?: boolean,
    palUnitIdentifier: PalBulkUnitIdentifier = 'unit_id'
): Array<{ label: string; key: string }> => {
    const identifierHeader = isPalBulkImport && palUnitIdentifier === 'pms_id' ? HeaderBulkTypes.PMS_ID : HeaderBulkTypes.UNIT_ID;
    const headers = [{ label: identifierHeader, key: identifierHeader }, ..._.map(sharedRequiredHeaders, (h) => ({ label: h, key: h }))];
    if (isPalBulkImport) {
        headers.push({ label: HeaderBulkTypes.REVIEW_RESPONSE, key: HeaderBulkTypes.REVIEW_RESPONSE });
    }
    return headers;
};

export const headersDefault: Array<{ label: string; key: string }> = getBulkCsvTemplateHeaders();

export const headersErrors: Array<{ label: string; key: string }> = [
    ...headersDefault,
    {
        label: 'Detail',
        key: 'Detail',
    },
];

export const getBulkCsvResultHeaders = (isPalBulkImport?: boolean): Array<string> => {
    const headers: Array<string> = [...requiredHeaders, 'Detail', 'ReviewID'];
    if (isPalBulkImport) {
        headers.splice(requiredHeaders.length, 0, HeaderBulkTypes.REVIEW_RESPONSE);
    }
    return headers;
};

export const headersResult: Array<string> = getBulkCsvResultHeaders();
